import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { api, formatNOK, DAY_SHORT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Trash2, Download, List, Users, Check, Info, GripVertical } from "lucide-react";
import { useContact } from "@/components/ContactModal";
import {
  TeamTour,
  TeamTourHelpButton,
  isTeamTourDismissed,
  markTeamTourDismissed,
  clearTeamTourDismissed,
} from "@/components/TeamTour";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exportCsv";
import FloatingPopover from "@/components/FloatingPopover";
import { PANEL_CLS, ROW_CLS, PAGE_EYEBROW_CLS, PAGE_H1_CLS, PRIMARY_BTN_CLS, SECONDARY_BTN_CLS } from "@/components/premium";

function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}

// Two-letter initials from a full name, e.g. "Nora Dahl" -> "ND".
const initialsOf = (name = "") =>
  name.trim().split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();

// Stable per-staff hue mapping for avatars / column accents. Uses the
// staff id hash modulo a curated palette so the same person gets the
// same colour every refresh, matching reference Nordic-SaaS calendars.
// Hues stay subtle in light, more luminous in dark — same approach as
// our STATUS_STYLES.
const STAFF_PALETTE = [
  { ring: "ring-blue-300/60 dark:ring-blue-400/40",       chip: "bg-blue-100/70 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",       lane: "border-blue-200/60 dark:border-blue-900/40" },
  { ring: "ring-emerald-300/60 dark:ring-emerald-400/40", chip: "bg-emerald-100/70 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200", lane: "border-emerald-200/60 dark:border-emerald-900/40" },
  { ring: "ring-amber-300/60 dark:ring-amber-400/40",     chip: "bg-amber-100/70 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",     lane: "border-amber-200/60 dark:border-amber-900/40" },
  { ring: "ring-violet-300/60 dark:ring-violet-400/40",   chip: "bg-violet-100/70 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200", lane: "border-violet-200/60 dark:border-violet-900/40" },
  { ring: "ring-rose-300/60 dark:ring-rose-400/40",       chip: "bg-rose-100/70 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",         lane: "border-rose-200/60 dark:border-rose-900/40" },
  { ring: "ring-cyan-300/60 dark:ring-cyan-400/40",       chip: "bg-cyan-100/70 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200",         lane: "border-cyan-200/60 dark:border-cyan-900/40" },
];
const staffPalette = (id = "") => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  return STAFF_PALETTE[Math.abs(h) % STAFF_PALETTE.length];
};

const STATUS_STYLES = {
  // booked — subtle blue accent
  booked: {
    card:
      "border-blue-200/60 border-l-[3px] border-l-blue-500/70 bg-blue-50/40 " +
      "dark:border-blue-900/30 dark:border-l-blue-400/70 dark:bg-blue-950/30",
    badge:
      "bg-blue-100/80 text-blue-800 " +
      "dark:bg-blue-900/50 dark:text-blue-200",
  },
  // completed — subtle emerald accent
  completed: {
    card:
      "border-emerald-200/60 border-l-[3px] border-l-emerald-500/70 bg-emerald-50/40 " +
      "dark:border-emerald-900/30 dark:border-l-emerald-400/70 dark:bg-emerald-950/30",
    badge:
      "bg-emerald-100/80 text-emerald-800 " +
      "dark:bg-emerald-900/50 dark:text-emerald-200",
  },
  // no_show — subtle amber accent
  no_show: {
    card:
      "border-amber-200/60 border-l-[3px] border-l-amber-500/70 bg-amber-50/40 " +
      "dark:border-amber-900/30 dark:border-l-amber-400/70 dark:bg-amber-950/30",
    badge:
      "bg-amber-100/80 text-amber-800 " +
      "dark:bg-amber-900/50 dark:text-amber-200",
  },
  // cancelled — subtle red/grey accent (most muted of the four)
  cancelled: {
    card:
      "border-rose-200/50 border-l-[3px] border-l-rose-400/60 bg-rose-50/30 " +
      "dark:border-rose-900/30 dark:border-l-rose-400/55 dark:bg-rose-950/25",
    badge:
      "bg-rose-100/80 text-rose-800 " +
      "dark:bg-rose-900/50 dark:text-rose-200",
  },
};
const STATUS_LABEL = { booked: "Booket", completed: "Fullført", no_show: "Ikke møtt", cancelled: "Avlyst" };

export default function Calendar() {
  const { user } = useAuth();
  const { open: openContact } = useContact();
  const isOwner = user?.role === "owner";
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ service_id: "", customer_name: "", customer_phone: "", customer_email: "", date: "", time: "10:00", notes: "", staff_id: "" });
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [team, setTeam] = useState([]);
  const [biz, setBiz] = useState(null);

  // View mode + staff filter — both client-side. selectedStaffIds is a
  // Set<string>; the special token "__unassigned" lets the user toggle
  // bookings that have no staff_id (legacy or "anyone" public bookings).
  // Default: all staff visible AND unassigned visible.
  const [viewMode, setViewMode] = useState("list");
  const [selectedStaffIds, setSelectedStaffIds] = useState(() => new Set());
  // Single source of truth for the calendar's current focus date. The
  // top week-picker (Liste view) and the Team day-picker (Team view) both
  // mutate this same value — `weekStart` is purely derived from it. That
  // makes it impossible for the two pickers to drift apart (the previous
  // implementation used two states + bidirectional effects and could end
  // up with the top header showing one week while the Team picker showed
  // a date months away).
  const [teamDay, setTeamDay] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const weekStart = useMemo(() => startOfWeek(teamDay), [teamDay]);
  // Stable ms key for effects that should only re-run when the *week*
  // (not the day within the week) changes — bookings are fetched per week.
  const weekStartMs = weekStart.getTime();

  // The Team toggle only makes sense once there are at least 2 people
  // on the team — solo Leder has nothing to compare lanes against.
  const teamModeAvailable = team.length > 1;
  // If team shrinks back to 1 person while we're in Team mode, slide back
  // to List so the UI never shows a single-column empty Team layout.
  useEffect(() => {
    if (!teamModeAvailable && viewMode === "team") setViewMode("list");
  }, [teamModeAvailable, viewMode]);

  // Seed the staff filter so "everyone + unassigned" is selected the first
  // time we get the team list. The user can toggle individuals after.
  //
  // Dashboard deep-link (`/kalender?staff=<id|me|all>`): when present,
  // we narrow the seed to just that staff member so the Calendar boots
  // with the exact same focus as the dashboard the user came from. The
  // query param is one-shot — toggling chips here doesn't push it back
  // to the URL, the next reload defaults to "everyone" again.
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardStaffParam = useMemo(() => {
    const sp = new URLSearchParams(location.search).get("staff");
    return sp && sp !== "all" ? sp : null;
  }, [location.search]);
  useEffect(() => {
    if (!team.length || selectedStaffIds.size !== 0) return;
    if (dashboardStaffParam) {
      const targetId = dashboardStaffParam === "me" ? user?.id : dashboardStaffParam;
      // Only honour the param if the id is actually a team member —
      // otherwise (stale link, wrong business) fall back to the default
      // "everyone" view so the page is never empty.
      if (targetId && team.some((t) => t.id === targetId)) {
        setSelectedStaffIds(new Set([targetId]));
        // If the team has 2+ members and we're narrowing to one, switch
        // to Team view so the user sees that person's lane prominently.
        if (team.length > 1) setViewMode("team");
        return;
      }
    }
    setSelectedStaffIds(new Set([...team.map((t) => t.id), "__unassigned"]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, selectedStaffIds.size, dashboardStaffParam, user?.id]);

  // Onboarding tour — opens automatically the first time a user lands in
  // Team mode, and is dismissible permanently per user. The help button
  // next to the view-mode toggle re-opens it manually.
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    if (viewMode === "team" && teamModeAvailable && !isTeamTourDismissed(user?.id)) {
      // Brief defer so the chips + toggle have committed to the DOM
      // before we measure them for the anchor rects.
      const t = setTimeout(() => setTourOpen(true), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [viewMode, teamModeAvailable, user?.id]);
  const closeTour = ({ dismiss } = {}) => {
    setTourOpen(false);
    if (dismiss) markTeamTourDismissed(user?.id);
  };
  const reopenTour = () => {
    clearTeamTourDismissed(user?.id);
    setTourOpen(true);
  };

  // Dashboard deep-link (`/kalender?new=1[&customer_id=...]`): auto-open
  // the "Ny booking" dialog and optionally pre-fill the customer fields
  // from an existing customer record. One-shot — we strip the params on
  // mount so a refresh / back-nav doesn't reopen the modal unexpectedly.
  useEffect(() => {
    const wantNew = searchParams.get("new") === "1";
    const customerId = searchParams.get("customer_id");
    if (!wantNew && !customerId) return;
    if (wantNew) setDialogOpen(true);
    if (customerId) {
      api.get(`/customers/${customerId}`)
        .then((r) => {
          const c = r.data?.customer || r.data;
          if (c) {
            setForm((f) => ({
              ...f,
              customer_name: c.name || "",
              customer_phone: c.phone || "",
              customer_email: c.email || "",
            }));
          }
        })
        .catch(() => { /* non-fatal — user just fills in manually */ });
    }
    // Consume the params so refresh/back doesn't re-trigger.
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    next.delete("customer_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, []);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 7); return d;
  }, [weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s, t] = await Promise.all([
        api.get("/bookings", { params: { start: weekStart.toISOString(), end: weekEnd.toISOString() } }),
        services.length ? { data: services } : api.get("/services"),
        team.length ? { data: team } : api.get("/staff").catch(() => ({ data: [] })),
      ]);
      setBookings(b.data);
      if (!services.length) setServices(s.data);
      if (!team.length) setTeam(t.data);
    } catch { toast.error("Klarte ikke å laste kalender"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStartMs]);

  // Load business once for plan/slug detection (used to upsell after a demo booking)
  useEffect(() => {
    api.get("/business").then((r) => setBiz(r.data)).catch(() => {});
  }, []);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });

  // Format a Date as YYYY-MM-DD using LOCAL getters. Using toISOString()
  // here would shift the date by one day in any timezone east of UTC
  // (e.g. Oslo CEST), so the grid column would sometimes show bookings on
  // the wrong weekday. Keep the comparison local on both sides.
  const ymdLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const bookingsByDay = days.map((d) => {
    const ds = ymdLocal(d);
    return bookings
      .filter((b) => ymdLocal(new Date(b.start_at)) === ds)
      .filter((b) => selectedStaffIds.has(b.staff_id || "__unassigned"))
      .sort((a, b) => a.start_at.localeCompare(b.start_at));
  });

  const staffById = useMemo(() => {
    const m = new Map();
    team.forEach(t => m.set(t.id, t));
    return m;
  }, [team]);

  // Bookings for the single day shown in Team mode (filtered the same way).
  const teamDayBookings = useMemo(() => {
    const ds = ymdLocal(teamDay);
    return bookings
      .filter((b) => ymdLocal(new Date(b.start_at)) === ds)
      .filter((b) => selectedStaffIds.has(b.staff_id || "__unassigned"));
  }, [bookings, teamDay, selectedStaffIds]);

  // Which staff get a lane in Team mode = selected ∩ team (preserving
  // team order). The "__unassigned" pseudo-id, when selected, surfaces
  // as an extra virtual "Ufordelt" lane to the right of real staff —
  // so newly-created bookings without a staff_id (e.g. "Hvem som helst")
  // are never invisible.
  const teamLanes = useMemo(
    () => team.filter(t => selectedStaffIds.has(t.id)),
    [team, selectedStaffIds]
  );
  const showUnassignedLane =
    selectedStaffIds.has("__unassigned") &&
    bookings.some(b => !b.staff_id && ymdLocal(new Date(b.start_at)) === ymdLocal(teamDay));
  const teamUnassignedToday = teamDayBookings.filter(b => !b.staff_id);

  // Toggle a staff member (or the "__unassigned" pseudo-id) in/out of the
  // filter set. Always returns a new Set so React sees the change.
  const toggleStaff = (id) => {
    setSelectedStaffIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // "Vis alle" / "Mine" presets.
  const showAllStaff = () => {
    setSelectedStaffIds(new Set([...team.map(t => t.id), "__unassigned"]));
  };
  const showOnlyMine = () => {
    if (user?.id) setSelectedStaffIds(new Set([user.id]));
  };

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}`, { status });
      toast.success("Booking oppdatert");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke oppdatere");
    }
  };

  // Reschedule + reassign in one PUT. Used by Team-mode drag-and-drop
  // (which may change either or both) and by the inline "Bytt ansatt"
  // popover (staff only). Reloads on success; rolls back optimistic UI
  // by reloading on failure too.
  const rescheduleBooking = async (id, { start_at, staff_id } = {}) => {
    const payload = {};
    if (start_at) payload.start_at = start_at;
    if (staff_id !== undefined) payload.staff_id = staff_id;
    if (!Object.keys(payload).length) return;
    try {
      await api.put(`/bookings/${id}`, payload);
      toast.success("Booking oppdatert");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke oppdatere");
      load();
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/bookings/${toDelete.id}`);
      toast.success("Booking slettet");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke slette");
    } finally { setDeleting(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_id || !form.customer_name || !form.date || !form.time) {
      return toast.error("Fyll inn tjeneste, navn, dato og tid");
    }
    const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
    try {
      const payload = {
        service_id: form.service_id,
        customer_name: form.customer_name.trim(),
        start_at: iso,
      };
      if (form.customer_phone?.trim()) payload.customer_phone = form.customer_phone.trim();
      if (form.customer_email?.trim()) payload.customer_email = form.customer_email.trim();
      if (form.notes?.trim()) payload.notes = form.notes.trim();
      if (form.staff_id) payload.staff_id = form.staff_id;
      await api.post("/bookings", payload);
      const isDemoBiz = biz?.plan === "demo";
      if (isDemoBiz) {
        toast.success("Booking opprettet — du har testet ClientFlow!", {
          description: "Klar for å gå live med din egen bedrift?",
          duration: 9000,
          action: {
            label: "Be om full tilgang",
            onClick: () =>
              openContact({
                source: "calendar-after-demo-booking",
                defaultMessage:
                  "Jeg har testet ClientFlow-demoen og vil gjerne snakke om oppstart.",
              }),
          },
        });
      } else {
        toast.success("Booking opprettet");
      }
      setDialogOpen(false);
      setForm({ service_id: "", customer_name: "", customer_phone: "", customer_email: "", date: "", time: "10:00", notes: "", staff_id: "" });
      load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error(err.response.data?.detail || "Denne tiden er allerede booket");
      } else if (status === 402) {
        toast.error("Demo-plan er brukt opp denne måneden.", {
          description: "Be om tilgang til Basic for å lage flere bookinger.",
          duration: 9000,
          action: {
            label: "Be om tilgang",
            onClick: () =>
              openContact({
                source: "calendar-quota-exceeded",
                defaultMessage:
                  "Demo-grensen er nådd og vi vil oppgradere til Basic.",
              }),
          },
        });
      } else {
        toast.error(err?.response?.data?.detail || "Kunne ikke opprette");
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className={PAGE_EYEBROW_CLS}>Kalender</p>
          <h1 className={PAGE_H1_CLS}>Bookinger</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" className={`${SECONDARY_BTN_CLS} w-10 px-0`} data-testid="prev-week"
            onClick={() => { const d = new Date(teamDay); d.setDate(d.getDate() - 7); setTeamDay(d); }}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" className={SECONDARY_BTN_CLS} data-testid="today-btn"
            onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setTeamDay(d); }}>I dag</Button>
          <Button variant="outline" size="icon" className={`${SECONDARY_BTN_CLS} w-10 px-0`} data-testid="next-week"
            onClick={() => { const d = new Date(teamDay); d.setDate(d.getDate() + 7); setTeamDay(d); }}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          {isOwner && (
            <Button
              variant="outline"
              className={SECONDARY_BTN_CLS}
              onClick={() => downloadCsv("/export/bookings.csv", "bookinger.csv", "bookinger")}
              data-testid="export-bookings-btn"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Eksporter bookinger</span>
              <span className="sm:hidden">Eksporter</span>
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className={PRIMARY_BTN_CLS} data-testid="new-booking-btn">
                <Plus className="w-4 h-4" strokeWidth={2.25} /> <span className="hidden sm:inline">Ny booking</span><span className="sm:hidden">Ny</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Ny booking</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Tjeneste</Label>
                  <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                    <SelectTrigger className="rounded-xl mt-1.5" data-testid="booking-service-select">
                      <SelectValue placeholder="Velg tjeneste" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} · {s.duration_min} min · {formatNOK(s.price_nok)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kundens navn</Label>
                  <Input className="rounded-xl mt-1.5" value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="booking-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Telefon</Label>
                    <Input className="rounded-xl mt-1.5" value={form.customer_phone}
                      onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="booking-phone-input" /></div>
                  <div><Label>E-post</Label>
                    <Input type="email" className="rounded-xl mt-1.5" value={form.customer_email}
                      onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="booking-email-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Dato</Label>
                    <Input type="date" className="rounded-xl mt-1.5" value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="booking-date-input" /></div>
                  <div><Label>Tid</Label>
                    <Input type="time" className="rounded-xl mt-1.5" value={form.time}
                      onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="booking-time-input" /></div>
                </div>
                {teamModeAvailable && (
                  <div>
                    <Label>Ansatt</Label>
                    <Select
                      value={form.staff_id || "__any"}
                      onValueChange={(v) => setForm({ ...form, staff_id: v === "__any" ? "" : v })}
                    >
                      <SelectTrigger className="rounded-xl mt-1.5" data-testid="booking-staff-select">
                        <SelectValue placeholder="Hvem som helst" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__any">Hvem som helst</SelectItem>
                        {team.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}{m.role === "owner" ? " · Leder" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" className={`w-full ${PRIMARY_BTN_CLS} h-11`} data-testid="booking-submit-btn">
                  Opprett booking
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Uke {Math.ceil(((weekStart - new Date(weekStart.getFullYear(), 0, 1)) / 86400000 + 1) / 7)} ·{" "}
        {weekStart.toLocaleDateString("nb-NO", { day: "2-digit", month: "long" })} —{" "}
        {new Date(weekEnd.getTime() - 1).toLocaleDateString("nb-NO", { day: "2-digit", month: "long", year: "numeric" })}
      </p>

      {team.length > 0 && (
        <div className="flex items-start justify-between gap-3 flex-wrap" data-testid="calendar-team-strip">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mr-1">Team:</span>
            {/* Filter chips per team member. Selected = filled accent.
                Click toggles in/out of the visible set. */}
            {team.map((m) => {
              const selected = selectedStaffIds.has(m.id);
              const p = staffPalette(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleStaff(m.id)}
                  data-testid={`staff-chip-${m.id}`}
                  className={`group inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border transition-all text-xs ${
                    selected
                      ? `bg-card border-foreground/15 shadow-sm ${p.chip}`
                      : "bg-transparent border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
                  }`}
                  aria-pressed={selected}
                >
                  <span className={`w-5 h-5 rounded-full ring-2 ${selected ? p.ring : "ring-transparent"} overflow-hidden bg-accent/70 flex items-center justify-center text-[9px] font-semibold text-primary`}>
                    {m.avatar_url ? (
                      <img src={m.avatar_url} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      initialsOf(m.name)
                    )}
                  </span>
                  <span className="font-medium">
                    {m.name.split(" ")[0]}
                    {m.role === "owner" ? <span className="ml-1 opacity-60">· Leder</span> : null}
                  </span>
                  {selected && <Check className="w-3 h-3 opacity-70" strokeWidth={2.25} />}
                </button>
              );
            })}
            {/* "Mine" / "Alle" presets — never disable, always one click away. */}
            {user?.id && (
              <button
                onClick={showOnlyMine}
                className="text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                data-testid="staff-preset-mine"
              >
                Mine
              </button>
            )}
            <button
              onClick={showAllStaff}
              className="text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
              data-testid="staff-preset-all"
            >
              Alle
            </button>
          </div>
          {/* View mode toggle — only when team makes Team mode meaningful. */}
          {teamModeAvailable && (
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-xl border border-border bg-card p-0.5" data-testid="view-mode-toggle">
              <button
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  viewMode === "list"
                    ? "bg-foreground/5 text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="view-mode-list"
              >
                <List className="w-3.5 h-3.5" strokeWidth={1.75} />
                Liste
              </button>
              <button
                onClick={() => setViewMode("team")}
                aria-pressed={viewMode === "team"}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  viewMode === "team"
                    ? "bg-foreground/5 text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="view-mode-team"
              >
                <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
                Team
              </button>
              </div>
              {viewMode === "team" && (
                <TeamTourHelpButton onClick={reopenTour} />
              )}
            </div>
          )}
        </div>
      )}

      {viewMode === "list" ? (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="calendar-list-view">
        {days.map((d, i) => {
          const isToday = new Date().toDateString() === d.toDateString();
          const empty = bookingsByDay[i].length === 0;
          // On mobile, hide empty non-today days to reduce scroll
          const hideOnMobile = empty && !isToday;
          return (
            <div
              key={i}
              className={`rounded-2xl border p-3 min-h-[120px] md:min-h-[140px] ${isToday ? "border-emerald-500/40 bg-emerald-500/[0.06] ring-1 ring-emerald-500/20" : "border-emerald-500/10 bg-card"} ${hideOnMobile ? "hidden md:block" : ""}`}
              data-testid={`day-col-${i}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">{DAY_SHORT[i]}{isToday && " · I DAG"}</span>
                <span className="font-heading text-xl font-bold">{d.getDate()}</span>
              </div>
              {bookingsByDay[i].length === 0 ? (
                <p className="text-xs text-muted-foreground mt-4">Ingen bookinger</p>
              ) : (
                <ul className="space-y-2">
                  {bookingsByDay[i].map((b) => {
                    const styles = STATUS_STYLES[b.status];
                    const staffMember = b.staff_id ? staffById.get(b.staff_id) : null;
                    return (
                      <li
                        key={b.id}
                        className={`rounded-xl border p-2.5 text-xs transition-colors ${styles.card}`}
                        data-testid="booking-card"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-foreground/55 text-[11px] font-medium tabular-nums">
                              {new Date(b.start_at).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                            <div className="mt-0.5 font-semibold text-foreground truncate">{b.customer_name}</div>
                            <div className="text-foreground/65 truncate">{b.service_name}</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {teamModeAvailable && staffMember && (
                              <span
                                title={staffMember.name}
                                className={`w-5 h-5 rounded-full ring-2 ${staffPalette(staffMember.id).ring} overflow-hidden bg-accent/70 flex items-center justify-center text-[9px] font-semibold text-primary`}
                                data-testid="booking-card-staff-dot"
                              >
                                {staffMember.avatar_url ? (
                                  <img src={staffMember.avatar_url} alt={staffMember.name} className="w-full h-full object-cover" />
                                ) : (
                                  initialsOf(staffMember.name)
                                )}
                              </span>
                            )}
                            {isOwner && (
                              <button
                                onClick={() => setToDelete(b)}
                                className="-mr-1 p-1 rounded-md text-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                                aria-label="Slett booking"
                                data-testid="delete-booking-btn"
                              >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold tracking-tight ${styles.badge}`}
                            data-testid={`booking-status-${b.status}`}
                          >
                            {STATUS_LABEL[b.status]}
                          </span>
                          {b.status === "booked" && (
                            <div className="ml-auto flex items-center gap-1">
                              <button
                                onClick={() => changeStatus(b.id, "completed")}
                                className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors whitespace-nowrap"
                                data-testid="mark-completed"
                              >
                                Fullført
                              </button>
                              <button
                                onClick={() => changeStatus(b.id, "no_show")}
                                className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors whitespace-nowrap"
                                data-testid="mark-noshow"
                              >
                                Ikke møtt
                              </button>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
        </div>
      ) : (
        <TeamDayView
          team={teamLanes}
          allTeam={team}
          showUnassignedLane={showUnassignedLane}
          day={teamDay}
          setDay={setTeamDay}
          bookings={teamDayBookings}
          unassigned={teamUnassignedToday}
          isOwner={isOwner}
          changeStatus={changeStatus}
          rescheduleBooking={rescheduleBooking}
          setToDelete={setToDelete}
        />
      )}
      {loading && <p className="text-sm text-muted-foreground">Laster...</p>}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-booking-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Slett denne bookingen?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && (
                <span className="block">
                  <strong>{toDelete.customer_name}</strong> · {toDelete.service_name}
                  <span className="block mt-1">
                    {new Date(toDelete.start_at).toLocaleString("nb-NO", { dateStyle: "full", timeStyle: "short" })}
                  </span>
                </span>
              )}
              <span className="block mt-3 text-sm">
                Tidsluken frigjøres umiddelbart. Kunden får ingen automatisk varsel.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="delete-booking-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-booking-confirm"
            >
              {deleting ? "Sletter..." : "Slett booking"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <TeamTour open={tourOpen} onClose={closeTour} />
    </div>
  );
}


// ----------------------------------------------------------------------
// Team / Tidslinje-visning
// ----------------------------------------------------------------------
// Single-day timeline with one lane per selected staff member. Bookings
// are absolutely-positioned blocks scaled by `pxPerMinute`, so a 30-min
// booking is half as tall as a 60-min one. Time grid runs 08:00–20:00
// by default — covers every salon/clinic opening window we've seen.
//
// Mobile: lanes are scrollable horizontally with a fixed lane width, so
// users swipe through the team rather than squeezing 4 columns into a
// 390px viewport. The time gutter on the left stays sticky.
// ----------------------------------------------------------------------

const TIMELINE_START_HOUR = 8;   // 08:00
const TIMELINE_END_HOUR = 20;    // 20:00
const PX_PER_MINUTE = 1.6;       // 96px per hour — readable, dense enough
const SNAP_MINUTES = 15;         // Drag snaps to quarter-hours.
const HOUR_HEIGHT = 60 * PX_PER_MINUTE;
const TIMELINE_HEIGHT = (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * HOUR_HEIGHT;

function TeamDayView({ team, allTeam, showUnassignedLane, day, setDay, bookings, unassigned, isOwner, changeStatus, rescheduleBooking, setToDelete }) {
  const todayY = useNowLineY(day);
  const dayLabel = day.toLocaleDateString("nb-NO", { weekday: "long", day: "2-digit", month: "long" });
  const isShowingToday = ymdLocalDate(day) === ymdLocalDate(new Date());

  const shiftDay = (offsetDays) => {
    const d = new Date(day);
    d.setDate(d.getDate() + offsetDays);
    d.setHours(0, 0, 0, 0);
    setDay(d);
  };

  // Group bookings by lane (staff_id). Bookings without staff_id collect
  // under the "__unassigned" key so the virtual "Ufordelt" lane can pick
  // them up. (Previously these were footer-only — easy to miss.)
  const byStaff = new Map();
  team.forEach(t => byStaff.set(t.id, []));
  byStaff.set("__unassigned", []);
  bookings.forEach(b => {
    const key = b.staff_id || "__unassigned";
    if (byStaff.has(key)) byStaff.get(key).push(b);
  });

  // The "lanes" we'll render — real staff first, then the virtual
  // Ufordelt lane only when there's something in it (or the filter chip
  // is explicitly enabled with bookings present).
  const lanes = [...team];
  if (showUnassignedLane) {
    lanes.push({ id: "__unassigned", name: "Ufordelt", role: "virtual", _virtual: true });
  }

  // Lane min-width: tighter on phones (so 2 lanes fit before scrolling),
  // roomier on tablets/desktops. Lanes always scroll horizontally past
  // the visible area when there are many staff.
  const laneMinWidth = "minmax(160px, 1fr)";

  return (
    <div className="space-y-3" data-testid="calendar-team-view">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-xl" onClick={() => shiftDay(-1)} data-testid="team-prev-day">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            className="rounded-xl px-3"
            onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setDay(d); }}
            data-testid="team-today-btn"
          >
            I dag
          </Button>
          <Button variant="outline" size="icon" className="rounded-xl" onClick={() => shiftDay(1)} data-testid="team-next-day">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground capitalize">
          {dayLabel}
        </p>
      </div>

      {lanes.length === 0 ? (
        <div className={`${PANEL_CLS} p-6 text-sm text-muted-foreground text-center`}>
          Ingen ansatte valgt. Velg én eller flere chips over for å vise lanes.
        </div>
      ) : (
        <div className={`${PANEL_CLS} overflow-hidden`}>
          {/* Single horizontal scroll container wraps BOTH the lane
              headers and the body timeline grid — that way swiping
              right on mobile reveals more lanes including their
              header labels. Previously the headers row was outside
              the scroll container, so the lane labels stayed put
              while only the bookings scrolled — disorienting. */}
          <div className="overflow-x-auto touch-pan-x" style={{ WebkitOverflowScrolling: "touch" }}>
            <div style={{ minWidth: `${60 + lanes.length * 160}px` }}>
              {/* Lane headers — repeated grid template so the time gutter +
                  every lane lines up exactly with the body grid below. */}
              <div className="grid" style={{ gridTemplateColumns: `60px repeat(${lanes.length}, ${laneMinWidth})` }}>
                <div className="border-b border-border bg-muted/40" />
                {lanes.map((m) => {
                  const isVirtual = m._virtual;
                  const p = isVirtual
                    ? { lane: "border-border", ring: "ring-foreground/10", chip: "" }
                    : staffPalette(m.id);
                  return (
                    <div
                      key={m.id}
                      className={`flex items-center gap-2 px-3 py-2.5 border-b border-l first:border-l-0 ${p.lane} bg-muted/40`}
                      data-testid={`team-lane-header-${m.id}`}
                    >
                      <span className={`w-7 h-7 rounded-full ring-2 ${p.ring} overflow-hidden bg-accent/70 flex items-center justify-center text-[10px] font-semibold text-primary shrink-0`}>
                        {!isVirtual && m.avatar_url ? (
                          <img src={m.avatar_url} alt={m.name} className="w-full h-full object-cover" />
                        ) : (
                          isVirtual ? "?" : initialsOf(m.name)
                        )}
                      </span>
                      <div className="min-w-0">
                        <div className="font-heading font-semibold text-sm truncate">{m.name}</div>
                        <div className="text-[11px] text-muted-foreground -mt-0.5">
                          {isVirtual ? "Uten ansatt valgt" : (m.role === "owner" ? "Leder" : "Ansatt")}
                          {" · "}
                          {byStaff.get(m.id)?.length || 0} {byStaff.get(m.id)?.length === 1 ? "booking" : "bookinger"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="relative grid"
                style={{
                  gridTemplateColumns: `60px repeat(${lanes.length}, ${laneMinWidth})`,
                  height: TIMELINE_HEIGHT,
                }}
              >
              <div className="relative border-r border-border">
                {Array.from({ length: TIMELINE_END_HOUR - TIMELINE_START_HOUR + 1 }, (_, i) => {
                  const hour = TIMELINE_START_HOUR + i;
                  return (
                    <div
                      key={hour}
                      className="absolute left-0 right-0 text-[10px] text-muted-foreground tabular-nums pr-1.5 text-right -translate-y-1/2"
                      style={{ top: `${i * HOUR_HEIGHT}px` }}
                    >
                      {hour < 10 ? `0${hour}` : hour}:00
                    </div>
                  );
                })}
              </div>

              {lanes.map((m) => (
                <TeamLane
                  key={m.id}
                  staff={m}
                  allTeam={allTeam}
                  bookings={byStaff.get(m.id) || []}
                  isOwner={isOwner}
                  changeStatus={changeStatus}
                  rescheduleBooking={rescheduleBooking}
                  setToDelete={setToDelete}
                  isShowingToday={isShowingToday}
                  todayY={todayY}
                  dayDate={day}
                />
              ))}

              {isShowingToday && todayY != null && (
                <div
                  className="absolute left-[60px] right-0 h-px bg-rose-500/70 pointer-events-none"
                  style={{ top: `${todayY}px` }}
                  aria-hidden="true"
                >
                  <span className="absolute -left-12 -top-2 text-[10px] font-semibold text-rose-500 tabular-nums">
                    {new Date().toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {unassigned.length > 0 && !showUnassignedLane && (
        <div className="text-xs text-muted-foreground px-1" data-testid="team-unassigned-count">
          {unassigned.length} {unassigned.length === 1 ? "booking uten ansatt" : "bookinger uten ansatt"}
          {" — slå på «Ufordelt» i filteret for å se dem."}
        </div>
      )}
    </div>
  );
}

function TeamLane({ staff, allTeam, bookings, isOwner, changeStatus, rescheduleBooking, setToDelete, isShowingToday, todayY, dayDate }) {
  const isVirtual = staff?._virtual;
  const p = isVirtual
    ? { lane: "border-border", ring: "ring-foreground/10", chip: "" }
    : staffPalette(staff.id);
  const [dropMinutes, setDropMinutes] = useState(null);

  const computeDropMinutes = (e, durationMin, grabOffsetMin) => {
    const lane = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - lane.top;
    if (y < 0 || y > TIMELINE_HEIGHT) return null;
    const rawMinutes = y / PX_PER_MINUTE - (grabOffsetMin || 0);
    const snapped = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
    const maxStart = (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 60 - (durationMin || 30);
    return Math.max(0, Math.min(snapped, maxStart));
  };

  const onDragOver = (e) => {
    if (!isOwner) return;
    if (!e.dataTransfer.types.includes("application/x-cf-booking")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    try {
      const meta = JSON.parse(e.dataTransfer.getData("application/x-cf-booking") || "{}");
      const m = computeDropMinutes(e, meta.duration, meta.grabOffsetMin);
      setDropMinutes(m);
    } catch { /* ignore */ }
  };
  const onDragLeave = () => setDropMinutes(null);
  const onDrop = (e) => {
    if (!isOwner) return;
    e.preventDefault();
    setDropMinutes(null);
    let meta;
    try { meta = JSON.parse(e.dataTransfer.getData("application/x-cf-booking") || "{}"); } catch { return; }
    if (!meta?.id) return;
    const newMinutes = computeDropMinutes(e, meta.duration, meta.grabOffsetMin);
    if (newMinutes == null) return;
    const targetStart = new Date(dayDate);
    targetStart.setHours(TIMELINE_START_HOUR, 0, 0, 0);
    targetStart.setMinutes(targetStart.getMinutes() + newMinutes);
    const payload = { start_at: targetStart.toISOString() };
    // Empty string is the sentinel the backend treats as "clear back to
    // anyone" — used when dropping onto the virtual Ufordelt lane.
    const targetStaffId = isVirtual ? "" : staff.id;
    if ((meta.staffId || "") !== targetStaffId) payload.staff_id = targetStaffId;
    rescheduleBooking(meta.id, payload);
  };

  return (
    <div
      className={`relative border-l ${p.lane}`}
      data-testid={`team-lane-${staff.id}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {Array.from({ length: TIMELINE_END_HOUR - TIMELINE_START_HOUR }, (_, i) => (
        <div
          key={i}
          className="absolute left-0 right-0 border-t border-border/60"
          style={{ top: `${(i + 1) * HOUR_HEIGHT}px` }}
        />
      ))}
      {dropMinutes != null && (
        <div
          className="absolute left-1 right-1 rounded-lg border-2 border-dashed border-primary/60 bg-primary/5 pointer-events-none"
          style={{ top: `${dropMinutes * PX_PER_MINUTE}px`, height: `${30 * PX_PER_MINUTE}px` }}
          aria-hidden="true"
        />
      )}
      {bookings.map((b) => {
        const start = new Date(b.start_at);
        const startMinutes = start.getHours() * 60 + start.getMinutes() - TIMELINE_START_HOUR * 60;
        const top = startMinutes * PX_PER_MINUTE;
        const height = Math.max(28, b.duration_min * PX_PER_MINUTE - 2);
        const inRange = top >= 0 && top < TIMELINE_HEIGHT;
        if (!inRange) return null;
        const styles = STATUS_STYLES[b.status];
        const compact = height < 56;
        const draggable = isOwner && b.status !== "cancelled";
        const onDragStart = (e) => {
          if (!draggable) { e.preventDefault(); return; }
          const blockRect = e.currentTarget.getBoundingClientRect();
          const grabOffsetMin = Math.max(0, (e.clientY - blockRect.top) / PX_PER_MINUTE);
          const payload = {
            id: b.id,
            staffId: b.staff_id || null,
            duration: b.duration_min || 30,
            grabOffsetMin,
          };
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("application/x-cf-booking", JSON.stringify(payload));
          if (e.dataTransfer.setDragImage) {
            const img = new Image();
            img.src = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
            e.dataTransfer.setDragImage(img, 0, 0);
          }
        };
        return (
          <div
            key={b.id}
            className={`absolute left-1 right-1 rounded-lg border p-1.5 overflow-hidden ${styles.card} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
            style={{ top: `${top}px`, height: `${height}px` }}
            data-testid="team-booking-block"
            draggable={draggable}
            onDragStart={onDragStart}
          >
            <div className="flex items-start justify-between gap-1">
              <div className="min-w-0">
                <div className="text-[10px] text-foreground/55 tabular-nums leading-none">
                  {start.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
                  {!compact && b.duration_min ? <span className="opacity-60"> · {b.duration_min}m</span> : null}
                </div>
                <div className={`font-semibold text-foreground truncate ${compact ? "text-[11px] mt-0.5" : "text-xs mt-1"}`}>
                  {b.customer_name}
                </div>
                {!compact && (
                  <div className="text-[11px] text-foreground/65 truncate">{b.service_name}</div>
                )}
              </div>
              <div className="shrink-0 flex items-center">
                {/* Details popover is available on every block (incl. for
                    staff users) so kunden's notes & contact info are one
                    click away. Reassign + delete are owner-only. */}
                {!compact && (
                  <BookingDetailsPopover booking={b} />
                )}
                {isOwner && !compact && !isVirtual && (
                  <ReassignStaffPopover
                    booking={b}
                    allTeam={allTeam}
                    onReassign={(newId) => rescheduleBooking(b.id, { staff_id: newId })}
                  />
                )}
                {isOwner && !compact && (
                  <button
                    onClick={() => setToDelete(b)}
                    className="p-0.5 rounded text-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                    aria-label="Slett booking"
                    data-testid="delete-booking-btn"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.75} />
                  </button>
                )}
              </div>
            </div>
            {!compact && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                <span className={`inline-flex items-center px-1 py-px rounded text-[9px] font-semibold tracking-tight ${styles.badge}`}>
                  {STATUS_LABEL[b.status]}
                </span>
                {b.status === "booked" && height >= 80 && (
                  <div className="ml-auto flex items-center gap-0.5">
                    <button
                      onClick={() => changeStatus(b.id, "completed")}
                      className="px-1 py-px rounded text-[9px] font-semibold text-foreground/70 hover:text-foreground hover:bg-foreground/5 whitespace-nowrap"
                    >
                      Fullført
                    </button>
                    <button
                      onClick={() => changeStatus(b.id, "no_show")}
                      className="px-1 py-px rounded text-[9px] font-semibold text-foreground/70 hover:text-foreground hover:bg-foreground/5 whitespace-nowrap"
                    >
                      Ikke møtt
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {isShowingToday && todayY != null && (
        <div className="absolute left-0 right-0 h-px bg-rose-500/70 pointer-events-none" style={{ top: `${todayY}px` }} aria-hidden="true" />
      )}
    </div>
  );
}

/**
 * Small popover showing the customer's notes + contact info for one
 * booking. Visible to anyone who can see the booking in the calendar
 * (Leder/admin always; staff for their own bookings). Read-only.
 */
function BookingDetailsPopover({ booking }) {
  const start = new Date(booking.start_at);
  return (
    <FloatingPopover
      width={272}
      maxHeight={420}
      align="end"
      testId="booking-details-popover"
      trigger={(props) => (
        <button
          {...props}
          className="p-0.5 rounded text-foreground/50 hover:text-foreground hover:bg-foreground/5 transition-colors"
          aria-label="Vis detaljer"
          data-testid={`booking-details-btn-${booking.id}`}
        >
          <Info className="w-3 h-3" strokeWidth={1.75} />
        </button>
      )}
    >
      <div className="p-3 text-left">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
          Booking
        </div>
        <div className="mt-1 font-heading font-semibold text-sm">{booking.customer_name}</div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {start.toLocaleString("nb-NO", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
          {booking.duration_min ? <span> · {booking.duration_min} min</span> : null}
        </div>
        <div className="mt-2 text-xs text-foreground">{booking.service_name}</div>
        {(booking.customer_phone || booking.customer_email) && (
          <div className="mt-3 space-y-0.5 text-xs">
            {booking.customer_phone && (
              <div><span className="text-muted-foreground">Telefon:</span> <span className="text-foreground tabular-nums">{booking.customer_phone}</span></div>
            )}
            {booking.customer_email && (
              <div className="break-all"><span className="text-muted-foreground">E-post:</span> <span className="text-foreground">{booking.customer_email}</span></div>
            )}
          </div>
        )}
        {booking.notes ? (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">
              Notat fra kunden
            </div>
            <div className="rounded-md bg-muted/40 p-2 text-xs text-foreground whitespace-pre-wrap">
              {booking.notes}
            </div>
          </div>
        ) : (
          <div className="mt-3 text-xs text-muted-foreground italic">
            Ingen notat fra kunden.
          </div>
        )}
      </div>
    </FloatingPopover>
  );
}


/**
 * Tiny popover for inline staff reassignment from a Team booking block.
 * Lists every team member (including "Hvem som helst") and the current
 * assignee is checkmarked. Click → parent calls PUT /bookings with the
 * new staff_id. Keeping this scoped to the Team timeline so the Liste
 * mode stays as visual-only as before.
 */
function ReassignStaffPopover({ booking, allTeam, onReassign }) {
  return (
    <FloatingPopover
      width={208}
      maxHeight={300}
      align="end"
      testId="reassign-staff-menu"
      trigger={(props) => (
        <button
          {...props}
          className="p-0.5 rounded text-foreground/50 hover:text-foreground hover:bg-foreground/5 transition-colors"
          aria-label="Bytt ansatt"
          data-testid={`reassign-staff-btn-${booking.id}`}
        >
          <Users className="w-3 h-3" strokeWidth={1.75} />
        </button>
      )}
    >
      {({ close }) => (
        <div className="p-1">
          <button
            onClick={() => { onReassign(""); close(); }}
            className="flex items-center justify-between gap-2 w-full px-2 py-1.5 rounded text-left text-xs hover:bg-foreground/5 transition-colors"
          >
            <span className="text-muted-foreground">Hvem som helst</span>
            {!booking.staff_id && <Check className="w-3 h-3 text-primary" strokeWidth={2.25} />}
          </button>
          <div className="h-px bg-border/60 my-1" />
          {allTeam.map((m) => {
            const active = booking.staff_id === m.id;
            return (
              <button
                key={m.id}
                onClick={() => { onReassign(m.id); close(); }}
                className="flex items-center justify-between gap-2 w-full px-2 py-1.5 rounded text-left text-xs hover:bg-foreground/5 transition-colors"
                data-testid={`reassign-staff-option-${m.id}`}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className={`w-4 h-4 rounded-full ring-1 ${staffPalette(m.id).ring} overflow-hidden bg-accent/70 flex items-center justify-center text-[8px] font-semibold text-primary shrink-0`}>
                    {m.avatar_url ? (
                      <img src={m.avatar_url} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      initialsOf(m.name)
                    )}
                  </span>
                  <span className="truncate">{m.name}</span>
                </span>
                {active && <Check className="w-3 h-3 text-primary shrink-0" strokeWidth={2.25} />}
              </button>
            );
          })}
        </div>
      )}
    </FloatingPopover>
  );
}

// Local YYYY-MM-DD helper, scoped to TeamDayView so the component is
// self-contained even if the Calendar-level helper moves later.
function ymdLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Vertical "now" line position — refreshes every minute. Returns null if
// the day shown is not today or now is outside the visible time grid.
function useNowLineY(day) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (ymdLocalDate(day) !== ymdLocalDate(now)) return null;
  const minutes = now.getHours() * 60 + now.getMinutes() - TIMELINE_START_HOUR * 60;
  if (minutes < 0 || minutes > (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 60) return null;
  return minutes * PX_PER_MINUTE;
}
